import has from 'lodash/has';

import { defaultErrorState } from './form-constants';
import * as c from './form-constants';
import { createError } from './form-utils';

import * as v from '../../shared/utils/validators/validators';

import { CARD_TYPE_REGEX } from '../../shared/constants/credit-card';

interface Options {
  fieldName?: string;
  isRequired?: boolean;
}

interface Value {
  value?: string;
}

export function validateBasicField(value = '', options: Options = {}) {
  let errorState = { ...defaultErrorState };
  const { fieldName = '', isRequired = true } = options;

  if (!v.notEmpty(value) && isRequired) {
    errorState = createError(
      c.NOT_EMPTY,
      fieldName,
      errorState.failedValidations
    );
  }

  return errorState;
}

function validateName(value = '', options: Options = {}) {
  let errorState = { ...defaultErrorState };
  const { fieldName = c.NAME, isRequired = true } = options;
  if (!v.isValidName(value)) {
    errorState = createError(
      c.IS_VALID_NAME,
      fieldName,
      errorState.failedValidations
    );
  }

  if (!v.notEmpty(value) && isRequired) {
    errorState = createError(
      c.NOT_EMPTY,
      fieldName,
      errorState.failedValidations
    );
  }

  return errorState;
}

export function validateFirstName(value = '', options = {}) {
  return validateName(value, { fieldName: c.FIRST_NAME, ...options });
}

export function validateLastName(value = '', options = {}) {
  return validateName(value, { fieldName: c.LAST_NAME, ...options });
}

export function validateEmail(value = '', options: Options = {}) {
  let errorState = { ...defaultErrorState };
  const { fieldName = c.EMAIL, isRequired = true } = options;

  if (value && !v.isValidEmail(value)) {
    errorState = createError(
      c.IS_VALID_EMAIL,
      fieldName,
      errorState.failedValidations
    );
  }
  if (!v.emailContainsValidDomain(value)) {
    errorState = createError(
      c.REQUIRE_VALID_DOMAIN,
      fieldName,
      errorState.failedValidations
    );
  }
  if (!v.emailContainsTopLevelDomain(value)) {
    errorState = createError(
      c.REQUIRE_TOP_LEVEL_DOMAIN,
      fieldName,
      errorState.failedValidations
    );
  }
  if (!v.emailContainsDomainName(value)) {
    errorState = createError(
      c.REQUIRE_DOMAIN_NAME,
      fieldName,
      errorState.failedValidations
    );
  }
  if (!v.emailContainsAt(value)) {
    errorState = createError(
      c.REQUIRE_AT,
      fieldName,
      errorState.failedValidations
    );
  }
  if (v.hasInvalidSpecialCharacterOnEmail(value)) {
    errorState = createError(
      c.HAS_INVALID_SPECIAL_CHARACTER_ON_EMAIL,
      fieldName,
      errorState.failedValidations
    );
  }
  if (!v.notEmpty(value) && isRequired) {
    errorState = createError(
      c.NOT_EMPTY,
      fieldName,
      errorState.failedValidations
    );
  }

  return errorState;
}

export function validatePhone(value = '', options: Options = {}) {
  let errorState = { ...defaultErrorState };
  const { fieldName = c.PHONE, isRequired = true } = options;

  if (!v.isValidPhone(value)) {
    errorState = createError(
      c.IS_VALID_PHONE,
      fieldName,
      errorState.failedValidations
    );
  }

  if (v.notEmpty(value)) {
    const trimmedValue = value.trim().replace(/[^0-9]/g, '');
    if (trimmedValue.length === 9) {
      errorState = createError(
        c.IS_VALID_PHONE_NUMBER_LENGTH,
        fieldName,
        errorState.failedValidations
      );
    } else if (trimmedValue.length < 9) {
      errorState = createError(
        c.IS_VALID_PHONE_NUMBER_LENGTH,
        fieldName,
        errorState.failedValidations,
        `${10 - trimmedValue.length} numbers missing`
      );
    }
  }

  if (!v.notEmpty(value) && isRequired) {
    errorState = createError(
      c.NOT_EMPTY,
      fieldName,
      errorState.failedValidations
    );
  }

  return errorState;
}

export function validateSelect(value: Value = {}, options = {}) {
  const fieldValue = has(value, 'value') ? value.value : '';

  return validateBasicField(fieldValue, options);
}

export function validatePhoneType(value = {}, options = {}) {
  return validateSelect(value, { fieldName: c.PHONE_TYPE, ...options });
}

export function validateCountry(value = {}, options = {}) {
  return validateSelect(value, { fieldName: c.COUNTRY, ...options });
}

export function validateState(value = {}, options = {}) {
  return validateSelect(value, { fieldName: c.STATE, ...options });
}

export function validateAddressLine1(value = '', options: Options = {}) {
  let errorState = { ...defaultErrorState };
  const { fieldName = c.ADDRESS_LINE_1, isRequired = true } = options;

  if (value.length > 0) {
    if (!v.atLeastOneNumeric(value)) {
      errorState = createError(
        c.AT_LEAST_ONE_NUMERIC,
        fieldName,
        errorState.failedValidations
      );
    }
    if (!v.maxLength(value, c.MAX_CHARACTER_LENGTH)) {
      errorState = createError(
        c.MAX_LENGTH,
        fieldName,
        errorState.failedValidations
      );
    }

    if (!v.isValidAddress(value)) {
      errorState = createError(
        c.IS_VALID_ADDRESS,
        fieldName,
        errorState.failedValidations
      );
    } else if (v.hasInvalidSpecialCharacterOnAddress(value)) {
      errorState = createError(
        c.HAS_INVALID_SPECIAL_CHARACTER_ON_ADDRESS,
        fieldName,
        errorState.failedValidations
      );
    }
  }

  if (!v.notEmpty(value) && isRequired) {
    errorState = createError(
      c.NOT_EMPTY,
      fieldName,
      errorState.failedValidations
    );
  }

  return errorState;
}

export function validateAddressLine2(value = '', options: Options = {}) {
  let errorState = { ...defaultErrorState };
  const { fieldName = c.ADDRESS_LINE_2 } = options;

  if (value.length > 0) {
    if (v.hasInvalidSpecialCharacterOnAddress(value)) {
      errorState = createError(
        c.HAS_INVALID_SPECIAL_CHARACTER_ON_ADDRESS,
        fieldName,
        errorState.failedValidations
      );
    }
    if (!v.maxLength(value, c.MAX_CHARACTER_LENGTH)) {
      errorState = createError(
        c.MAX_LENGTH,
        fieldName,
        errorState.failedValidations
      );
    }
  }
  return errorState;
}

export function validateZip(value = '', options: Options = {}) {
  let errorState = { ...defaultErrorState };
  const { fieldName = c.ZIP, isRequired = true } = options;

  if (!v.isValidPostalCode(value) && v.notEmpty(value)) {
    errorState = createError(
      c.IS_VALID_POSTAL_CODE,
      fieldName,
      errorState.failedValidations
    );
  }

  if (!v.notEmpty(value) && isRequired) {
    errorState = createError(
      c.NOT_EMPTY,
      fieldName,
      errorState.failedValidations
    );
  }

  return errorState;
}

export function validateCity(value = '', options: Options = {}) {
  let errorState = { ...defaultErrorState };
  const { fieldName = c.CITY, isRequired = true } = options;

  if (!v.notEmpty(value) && isRequired) {
    errorState = createError(
      c.NOT_EMPTY,
      fieldName,
      errorState.failedValidations
    );
  }

  return errorState;
}

export function validatePassword(value = '', options: Options = {}) {
  let errorState = { ...defaultErrorState };
  const { fieldName = c.PASSWORD, isRequired = true } = options;

  if (
    !v.minLength(value, c.MIN_PASSWORD_LENGTH) ||
    !v.maxLength(value, c.MAX_PASSWORD_LENGTH) ||
    !v.atLeastOneNumeric(value) ||
    !v.atLeastOneAplha(value)
  ) {
    errorState = createError(
      c.IS_VALID_PASSWORD,
      fieldName,
      errorState.failedValidations
    );
  }

  if (!v.notEmpty(value) && isRequired) {
    errorState = createError(
      c.NOT_EMPTY,
      fieldName,
      errorState.failedValidations
    );
  }

  return errorState;
}

export function validateCreditCard(value = '', options: Options = {}) {
  let errorState = { ...defaultErrorState };
  const { fieldName = c.CREDIT_CARD } = options;

  if (v.notEmpty(value)) {
    const trimmedValue = value.trim().replace(/[^0-9]/g, '');
    let cardLength = c.CARD_LENGTHS.NON_AMEX;
    let cardLengthErrorKey = c.IS_VALID_CREDIT_CARD_LENGTH;

    if (CARD_TYPE_REGEX.AMEX.test(value)) {
      cardLength = c.CARD_LENGTHS.AMEX;
      cardLengthErrorKey = c.IS_VALID_AMEX_CREDIT_CARD_LENGTH;
    }

    if (trimmedValue.length < cardLength - 1) {
      return createError(
        cardLengthErrorKey,
        fieldName,
        errorState.failedValidations,
        `${cardLength - trimmedValue.length} numbers missing`
      );
    }

    if (trimmedValue.length < cardLength) {
      return createError(
        cardLengthErrorKey,
        fieldName,
        errorState.failedValidations,
        'Missing number'
      );
    }

    if (!v.isValidCreditCard(value)) {
      return createError(
        c.IS_VALID_CREDIT_CARD,
        fieldName,
        errorState.failedValidations
      );
    }
  }

  return validateBasicField(value, options);
}

export function getDefaultValidation(type) {
  switch (type) {
    case c.TEXT:
      return validateBasicField;
    case c.FIRST_NAME:
      return validateFirstName;
    case c.LAST_NAME:
      return validateLastName;
    case c.EMAIL:
      return validateEmail;
    case c.PHONE:
      return validatePhone;
    case c.PHONE_TYPE:
      return validatePhoneType;
    case c.ADDRESS_LINE_1:
      return validateAddressLine1;
    case c.ADDRESS_LINE_2:
      return validateAddressLine2;
    case c.COUNTRY:
      return validateCountry;
    case c.ZIP:
      return validateZip;
    case c.CITY:
      return validateCity;
    case c.STATE:
      return validateState;
    case c.PASSWORD:
      return validatePassword;
    case c.CREDIT_CARD:
      return validateCreditCard;
    default:
      return validateBasicField;
  }
}

export default {
  validateAddressLine1,
  validateAddressLine2,
  validateBasicField,
  validateCity,
  validateCountry,
  validateCreditCard,
  validateEmail,
  validateFirstName,
  validateLastName,
  validatePassword,
  validatePhone,
  validatePhoneType,
  validateSelect,
  validateState,
  validateZip
};
